import { Link } from "@tanstack/react-router";
import { ArrowRight, Clock, Target, Users } from "lucide-react";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/ui/button";
import { CohortTimeConverter } from "@/components/landing/cohort-time-converter";
import { programmes } from "@/content/programmes";

export { programmes };

interface ProgrammesSectionProps {
  /** Homepage renders the condensed overview; /programmes renders the full listing. */
  condensed?: boolean;
}

export function ProgrammesSection({ condensed = false }: ProgrammesSectionProps) {
  const { t } = useTranslation();
  return (
    <section
      id="programmes"
      className="scroll-mt-24 border-y border-border bg-secondary/50 py-20"
    >
      <div className="mx-auto max-w-6xl px-5">
        <div className="max-w-2xl">
          <h2 className="text-3xl text-primary sm:text-4xl">{t("programmes.heading")}</h2>
          <p className="mt-3 text-muted-foreground">{t("programmes.intro")}</p>
        </div>

        <CohortTimeConverter />

        <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-4">
          {programmes.map((p) => {
            const title = t(`programmes.items.${p.slug}.title`, p.title);
            const band = t(`programmes.items.${p.slug}.band`, p.band);
            return (
            <article
              key={p.slug}
              className="surface-card flex flex-col p-7 transition-all duration-300 hover:-translate-y-1 hover:border-accent/50 hover:shadow-[var(--shadow-card)]"
            >
              <div className="flex flex-wrap items-center gap-2">
                <span className="inline-flex w-fit rounded-full bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-primary-foreground">
                  {band}
                </span>
                <span className="inline-flex w-fit items-center gap-1.5 rounded-full bg-accent/15 px-3 py-1 text-xs font-semibold text-accent">
                  <span className="size-1.5 animate-pulse rounded-full bg-accent" />
                  {t(`programmes.items.${p.slug}.spots`, p.spots)}
                </span>
              </div>
              <h3 className="mt-4 text-xl text-primary">
                <Link
                  to="/programmes/$slug"
                  params={{ slug: p.slug }}
                  className="transition-colors hover:text-accent"
                >
                  {title}
                </Link>
              </h3>
              <p className="mt-2 flex-1 text-sm leading-relaxed text-muted-foreground">
                {t(`programmes.items.${p.slug}.summary`, p.summary)}
              </p>

              <dl className="mt-4 space-y-2 text-sm text-muted-foreground">
                <div className="flex items-center gap-2">
                  <Target className="size-4 text-accent" />
                  <dt className="sr-only">{t("programmes.cefrLabel")}</dt>
                  <dd>{t("programmes.cefr", { level: p.cefr })}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="size-4 text-accent" />
                  <dt className="sr-only">{t("programmes.lessonLength")}</dt>
                  <dd>{t(`programmes.items.${p.slug}.duration`, p.duration)}</dd>
                </div>
                <div className="flex items-center gap-2">
                  <Users className="size-4 text-accent" />
                  <dt className="sr-only">{t("programmes.cohortSize")}</dt>
                  <dd>
                    {t(`programmes.items.${p.slug}.cohort`, p.cohort)}
                    {p.cohort !== "One-on-one" ? t("programmes.speakingTime") : ""}
                  </dd>
                </div>
              </dl>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <Button
                  variant="softOutline"
                  className="h-auto min-h-11 w-full whitespace-normal rounded-lg px-4 py-2.5 text-center leading-snug sm:w-auto"
                  asChild
                >
                  <Link
                    to="/programmes/$slug"
                    params={{ slug: p.slug }}
                    aria-label={t("programmes.exploreAria", { title, band })}
                  >
                    {t("programmes.explore", { title })}
                  </Link>
                </Button>

                {!condensed && (
                  <Link
                    to="/book"
                    aria-label={t("programmes.bookAria", { title, band })}
                    className="group inline-flex items-center gap-1.5 text-sm font-semibold text-primary transition-colors hover:text-accent"
                  >
                    {t("programmes.book")}
                    <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
            </article>
            );
          })}
        </div>

        {condensed && (
          <div className="mt-10">
            <Link
              to="/programmes"
              className="group inline-flex items-center gap-2 text-base font-semibold text-primary transition-colors hover:text-accent"
            >
              {t("programmes.compareAll")}
              <ArrowRight className="size-4 transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
